import { useEffect, useState } from 'react';

// Hard cap at 2 MB of raw text we'll pull into the renderer.
// Anything larger falls through to a "file too large — download instead"
// hint. The temp bucket caps uploads at 50 MB, but most of those should
// be binary; this is about DOM blowup on pathological text files.
const MAX_TEXT_BYTES = 2 * 1024 * 1024;

type State =
  | { status: 'loading' }
  | { status: 'ready'; text: string }
  | { status: 'too-large'; size: number }
  | { status: 'error'; message: string };

export function useTextContent(contentUrl: string): State {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });

    (async () => {
      try {
        const resp = await fetch(contentUrl, { signal: controller.signal });
        if (!resp.ok) {
          throw new Error(`S3 returned ${resp.status}`);
        }
        // Bail out early on oversize payloads so we don't hang the tab
        // reading a multi-gig text blob into memory.
        const contentLength = resp.headers.get('Content-Length');
        if (contentLength && Number(contentLength) > MAX_TEXT_BYTES) {
          setState({ status: 'too-large', size: Number(contentLength) });
          return;
        }
        const text = await resp.text();
        if (text.length > MAX_TEXT_BYTES) {
          setState({ status: 'too-large', size: text.length });
          return;
        }
        setState({ status: 'ready', text });
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setState({ status: 'error', message: (err as Error).message });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [contentUrl]);

  return state;
}
